// Google tag (Google Ads) helpers for the React SPA.
//
// Unlike src/lib/meta-pixel.ts, which calls `window.fbq` directly, this file is
// a thin facade over `window.compassGoogleAds` - the object the loader in
// index.html publishes. That indirection is deliberate: a Google Ads conversion
// needs BOTH the tag id and the conversion label, and calling gtag from here
// would mean repeating those literals in a third place, where they could drift
// from the two mirrors that a test actually pins. The loader owns the literals;
// this file owns the types and the guards.
//
// Every function below no-ops silently when the tag did not load - a blocked
// script, an ad blocker, or one of the denied capability paths. Same "inert
// rather than throwing" posture as `trackWeb` and `trackMetaStoreClick`: analytics
// must never break the page it is measuring.

interface CompassGoogleAds {
  /** An app-download CTA click. `eventId` is the cross-sink dedup id. */
  download: (props: { store: string; placement: string }, eventId?: string) => void
  /**
   * Optional because only the SPA mirror publishes it. The static-page loader in
   * public/analytics.js has no client-side routing, so a manual page_view there
   * would be a second count of the one gtag already sent on config.
   */
  pageView?: () => void
}

declare global {
  interface Window {
    compassGoogleAds?: CompassGoogleAds
  }
}

function getGoogleAds(): CompassGoogleAds | undefined {
  if (typeof window === 'undefined') return undefined
  const api = window.compassGoogleAds
  return api && typeof api.download === 'function' ? api : undefined
}

/** Google Ads conversion for an app-download CTA click. */
export function trackGoogleDownload(
  props: { store: string; placement: string },
  eventId: string,
): void {
  const api = getGoogleAds()
  if (!api) return
  try {
    api.download(props, eventId)
  } catch {
    // swallow - analytics must never break the page
  }
}

/** Manual page_view for SPA route changes (index.html fires the initial one). */
export function trackGooglePageView(): void {
  const api = getGoogleAds()
  if (!api || typeof api.pageView !== 'function') return
  try {
    api.pageView()
  } catch {
    // swallow - analytics must never break the page
  }
}
